import config from "../../../config/config"
import { GetSellerServicesUseCase } from "../../../domain/useCases/sellersServices/getSellersServices"
import { action, makeObservable, observable } from "mobx"
import { SellerServiceDetail } from "../../../domain/entities/SellerServiceDetail"
import { SellerService } from "../../../domain/entities/SellerService"
import { SearchSellersServicesUseCase } from "../../../domain/useCases/sellersServices/searchSellerServices"
import { ApiErrorCodes, ErrorHandler } from "../../error/ErrorHandler"
import { TableConfig } from "../../components/DataTable/types/TableConfig"
import { ExportTournamentUseCase } from "../../../domain/useCases/tournaments/exportTournament"
import { FileService } from "../../../domain/services/FileService"

interface ContructorParams {
	GetSellerServicesUseCase: GetSellerServicesUseCase
	SearchSellersServicesUseCase: SearchSellersServicesUseCase
	ExportTournamentUseCase: ExportTournamentUseCase
	ErrorHandler: ErrorHandler
}

export class TournamentsViewModel {
	private _getSellersServicesUseCase
	private _searchSellersServicesUseCase
	private _exportTournamentUseCase
	private _errorHandler
	isLoading: boolean = false
	isFetching: boolean = false
	isExporting: boolean = false
	hasNextPage: boolean = true
	searchedServices: Partial<SellerService>[] = []
	sellersServices: Partial<SellerService>[] = []
	selectedService: Partial<SellerServiceDetail> = {}
	sellerServices: Partial<SellerService>[] = []
	pagination: number = 0
	limit: number = 500
	searchValue: string = ""
	tableConfig: TableConfig = {
		pageSize: config.ui.componentsConfig.dataTablePageSize,
		sort: { order: "descend", field: "id" }
	}

	constructor({
		GetSellerServicesUseCase,
		SearchSellersServicesUseCase,
		ExportTournamentUseCase,
		ErrorHandler
	}: ContructorParams) {
		makeObservable(this, {
			searchValue: observable,
			isLoading: observable,
			sellersServices: observable,
			pagination: observable,
			setPagination: action,
			setSellerServices: action,
			isFetching: observable,
			isExporting: observable,
			hasNextPage: observable,
			setSellersServices: action,
			selectedService: observable,
			searchedServices: observable,
			setSearchedServices: action,
			setSelectedService: action,
			setLoading: action,
			setIsFetching: action,
			setIsExporting: action,
			setHasNextPage: action,
			updateServiceSessionState: action,
			tableConfig: observable,
			setTableConfig: action
		})
		this._getSellersServicesUseCase = GetSellerServicesUseCase
		this._searchSellersServicesUseCase = SearchSellersServicesUseCase
		this._exportTournamentUseCase = ExportTournamentUseCase
		this._errorHandler = ErrorHandler
		this.fetchTournaments()
	}

	public async fetchTournaments(params?: { pagination?: number; limit?: number }) {
		this.setLoading(true)
		try {
			const sellersServices = await this._getSellersServicesUseCase.exec({
				pagination: params?.pagination ?? 0,
				limit: params?.limit ?? this.limit,
				isTournament: true
			})
			this.setSellersServices(sellersServices)
			this.setSearchedServices(sellersServices)
			this.setHasNextPage(sellersServices.length > 0)
			this.setPagination(0)
		} catch (error) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async fetchPaginatedTournaments() {
		this.setIsFetching(true)
		try {
			const sellersServices = await this._getSellersServicesUseCase.exec({
				pagination: this.pagination,
				limit: this.limit,
				isTournament: true
			})
			if (sellersServices.length > 0) {
				const combinedSellerServices = [...sellersServices, ...this.sellersServices]
				this.setSellersServices(combinedSellerServices)
				this.setSearchedServices(combinedSellerServices)
			}
			this.setHasNextPage(sellersServices.length > 0)
		} catch (error) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setIsFetching(false)
		}
	}

	public async searchTournaments(expression: string) {
		if (expression === "") {
			this.setSearchedServices(this.sellersServices)
			return
		}
		this.setLoading(true)
		try {
			const searchedServices = await this._searchSellersServicesUseCase.exec({ expression, isTournament: true })
			this.setSearchedServices(searchedServices)
		} catch (error: any) {
			if (error?.errorCode === ApiErrorCodes.SERVICE_NOT_EXISTS) {
				this.setSearchedServices([])
				return
			}
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	setSellersServices(sellers: Partial<SellerService>[]) {
		this.sellersServices = sellers
	}

	setLoading(loading: boolean) {
		this.isLoading = loading
	}

	setIsFetching(loading: boolean) {
		this.isFetching = loading
	}

	updateServiceSessionState(service: Partial<SellerServiceDetail>) {
		const sellerServices = this.sellersServices.filter(serv => serv.id !== service.id)
		const sellerService = this.sellersServices.find(serv => String(serv.id) === String(service.id))
		if (sellerService) {
			this.setSellersServices([sellerService, ...sellerServices])
		}
	}

	setPagination(pagination: number) {
		this.pagination = pagination
	}

	setSelectedService(service: Partial<SellerServiceDetail>) {
		this.selectedService = service
	}

	setSearchedServices(services: Partial<SellerService>[]) {
		this.searchedServices = services
	}

	setHasNextPage(hasNextPage: boolean) {
		this.hasNextPage = hasNextPage
	}

	setLimit(limit: number) {
		this.limit = limit
	}
	setTableConfig(config: Partial<typeof this.tableConfig>) {
		this.tableConfig = { ...this.tableConfig, ...config }
	}

	setSellerServices(sellerServices: Partial<SellerService>[]) {
		this.sellerServices = sellerServices
	}

	setIsExporting(isExporting: boolean) {
		this.isExporting = isExporting
	}

	public async exportTournament(serviceId: number, tournamentTitle: string) {
		try {
			this.setIsExporting(true)
			const file = await this._exportTournamentUseCase.exec({ serviceId })
			const fileService = new FileService()
			const dateString = new Date().toISOString().split("T")[0]
			fileService.dowloadFile(file, `torneo-${tournamentTitle}-participantes-${dateString}.xlsx`)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setIsExporting(false)
		}
	}
}
